/**
 * The bookie's side of the board.
 *
 * A player wants to know what he stands to win. The man holding the money wants
 * the opposite question answered: if this lands, what does it cost me — and is
 * there an outcome that ruins my week.
 *
 * Two rules run through all of it.
 *
 * ONLY CONFIRMED MONEY COUNTS. A pending bet is an intention; the Venmo has not
 * landed. Folding those into exposure would have the bookie hedging against
 * money he does not hold, so they are reported separately and never mixed in.
 *
 * LIABILITY IS PROFIT, NOT PAYOUT. The stake is already in his pocket, so a
 * winning $25 bet at -150 costs him the $16.67 he hands back on top, not the
 * $41.67 that changes hands. Quoting payout as the damage double-counts money
 * he was never going to keep.
 */

import { payoutCents, profitCents } from './odds'

export interface BookBet {
  odds_id: string
  player_id: string
  stake_cents: number
  odds_at_placement: number
  status: 'pending' | 'confirmed' | 'won' | 'lost' | 'void'
}

export interface BookMarket {
  id: string
  market: string
  subject_id: string | null
  description: string
  american_odds: number
  status: 'proposed' | 'open' | 'closed' | 'settled'
  result: 'win' | 'lose' | 'void' | null
}

/** Money that is actually on a market — a confirmed bet, or one already graded. */
const isLive = (b: BookBet) => b.status !== 'pending' && b.status !== 'void'

export interface MarketExposure {
  marketId: string
  /** Confirmed stake taken on this market. */
  handleCents: number
  /** Stake sitting unconfirmed — not money yet. */
  pendingCents: number
  /** What goes back out if it lands, stake included. */
  payoutIfWinCents: number
  /** What it actually costs the house if it lands: the profit, not the payout. */
  liabilityCents: number
  /** Kept if it does not land. */
  keptIfLoseCents: number
  backers: number
}

export function marketExposure(market: BookMarket, bets: BookBet[]): MarketExposure {
  const mine = bets.filter((b) => b.odds_id === market.id)
  const live = mine.filter(isLive)

  const handleCents = live.reduce((n, b) => n + b.stake_cents, 0)
  const pendingCents = mine
    .filter((b) => b.status === 'pending')
    .reduce((n, b) => n + b.stake_cents, 0)

  // Each slip settles at the price it was taken at, which is why the board's
  // current line cannot be used here — moving a line must never re-price a bet
  // somebody already has.
  const payoutIfWinCents = live.reduce(
    (n, b) => n + payoutCents(b.stake_cents, b.odds_at_placement),
    0,
  )
  const liabilityCents = live.reduce(
    (n, b) => n + profitCents(b.stake_cents, b.odds_at_placement),
    0,
  )

  return {
    marketId: market.id,
    handleCents,
    pendingCents,
    payoutIfWinCents,
    liabilityCents,
    keptIfLoseCents: handleCents,
    backers: new Set(live.map((b) => b.player_id)).size,
  }
}

export interface BookSummary {
  /** Confirmed stake across every market still capable of paying out. */
  handleCents: number
  pendingCents: number
  /**
   * Every live slip, confirmed or not.
   *
   * `handleCents` counts only money that has actually arrived, which is the
   * right basis for exposure and a terrible one for "is anybody betting?" — a
   * board where nine slips are waiting on Venmo reported nothing taken and
   * looked dead. This is the activity figure; the other is the money figure.
   */
  acceptedCents: number
  /**
   * The worst the book can end up, NET — what leaves on top of the stakes it
   * keeps, which is what the screen has always said it means.
   *
   * Two corrections live in this number. It used to sum every open market's
   * liability, which is right for props and nonsense for a field of outrights:
   * thirteen men priced to win the same thing cannot all win it, so the sum
   * was a figure with no outcome behind it. The exclusive family now
   * contributes its single worst outcome and independent props are summed on
   * top, because those really can all land.
   *
   * And that worst outcome is now NET. It was the winning market's profit
   * alone, ignoring the stakes every other backer leaves behind — so the panel
   * printed "$3,750" directly above its own sentence saying the outcome that
   * hurts most is −$3,600, the $150 difference being the four losing slips.
   * Two definitions of at-risk, one panel. This is the one the caption gives
   * and the one `exclusiveOutcomes` already used.
   *
   * Floored at zero: a book whose every outcome is a profit risks nothing, and
   * a negative "at risk" reads as a loss to somebody scanning.
   */
  worstCaseCents: number
  /** Already banked or already paid out, netted. Positive means the house is up. */
  settledCents: number
  /*
   * The two halves behind that net — Scott's ask, because "up $2,075" hides
   * whether the house has been lucky or simply hasn't paid anybody yet. Kept
   * is the stakes on markets that lost; paid is the profit handed over on
   * markets that won; the counts say across how many rulings.
   */
  settledKeptCents: number
  settledPaidCents: number
  settledForHouse: number
  settledForPlayers: number
  openMarkets: number
}

/**
 * Markets that cannot both land, and which family they belong to.
 *
 * An outright names a player to win the thing and exactly one player does —
 * but so does any other "who leads X" board, and the book grew a second one
 * the day it priced the field for net birdies. So a family is any market
 * KIND that names a subject: all the outrights are one family, all the net
 * birdie lines another, and each contributes only its own worst outcome.
 * A prop with nobody's name on it is its own question and answers it alone.
 */
const familyOf = (m: BookMarket): string | null =>
  m.subject_id != null ? m.market : null

export function bookSummary(markets: BookMarket[], bets: BookBet[]): BookSummary {
  let handleCents = 0
  let pendingCents = 0
  let settledCents = 0
  let settledKeptCents = 0
  let settledPaidCents = 0
  let settledForHouse = 0
  let settledForPlayers = 0
  let openMarkets = 0
  // Each exclusive family contributes one outcome; the rest can all land.
  let independent = 0
  // Per family, so a winner's outcome nets against the stakes his rivals'
  // backers leave behind — and never against another board's.
  const families = new Map<string, { handle: number; liability: number }[]>()
  const familyHandle = new Map<string, number>()

  for (const m of markets) {
    const e = marketExposure(m, bets)
    pendingCents += e.pendingCents

    if (m.status === 'settled') {
      // Graded: a win cost the profit, a loss kept the stakes, a void is a wash.
      if (m.result === 'win') {
        settledCents -= e.liabilityCents
        settledPaidCents += e.liabilityCents
        if (e.handleCents > 0 || e.liabilityCents > 0) settledForPlayers += 1
      } else if (m.result === 'lose') {
        settledCents += e.handleCents
        settledKeptCents += e.handleCents
        if (e.handleCents > 0) settledForHouse += 1
      }
      continue
    }

    openMarkets += 1
    handleCents += e.handleCents
    const fam = familyOf(m)
    if (fam) {
      families.set(fam, [
        ...(families.get(fam) ?? []),
        { handle: e.handleCents, liability: e.liabilityCents },
      ])
      familyHandle.set(fam, (familyHandle.get(fam) ?? 0) + e.handleCents)
    } else independent += e.liabilityCents
  }

  /*
   * The deepest hole any single winner digs: his profit, less every rival
   * backer's stake — which the house keeps precisely because that man lost.
   * A prop's stake is NOT counted as kept: in the worst case the props land
   * too, which is what `independent` adds on top.
   */
  let worstExclusiveNet = 0
  for (const [fam, members] of families) {
    const handle = familyHandle.get(fam) ?? 0
    worstExclusiveNet += members.reduce(
      (worst, k) => Math.max(worst, k.liability - (handle - k.handle)),
      0,
    )
  }

  return {
    handleCents,
    pendingCents,
    acceptedCents: handleCents + pendingCents,
    worstCaseCents: Math.max(0, worstExclusiveNet + independent),
    settledCents,
    settledKeptCents,
    settledPaidCents,
    settledForHouse,
    settledForPlayers,
    openMarkets,
  }
}

export interface OutcomeRow {
  market: BookMarket
  exposure: MarketExposure
  /**
   * The house's net across the WHOLE book if this one lands and its siblings do
   * not — which is the only reading that makes sense for a field of outrights,
   * since exactly one man wins the thing.
   */
  houseNetCents: number
}

/**
 * What each outcome does to the house, for a set of mutually exclusive markets.
 *
 * Summing every market's liability is right for independent props and badly
 * wrong for a field of outrights: only one player can win, so the honest
 * question is "if Blackham lands, what happens" — he gets paid and every other
 * backer's stake stays put. That is a different, usually much smaller, number
 * than the worst case, and it is the one worth staring at before shortening a
 * price.
 */
export function exclusiveOutcomes(
  markets: BookMarket[],
  bets: BookBet[],
): OutcomeRow[] {
  const open = markets.filter((m) => m.status !== 'settled')
  const totalHandle = open.reduce(
    (n, m) => n + marketExposure(m, bets).handleCents,
    0,
  )

  return open
    .map((market) => {
      const exposure = marketExposure(market, bets)
      // Keep everyone else's stakes, hand this one its profit back.
      const houseNetCents = totalHandle - exposure.handleCents - exposure.liabilityCents
      return { market, exposure, houseNetCents }
    })
    .sort((a, b) => a.houseNetCents - b.houseNetCents)
}
